import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Copy, RefreshCw, ArrowLeft, Send, Settings, Map, TestTubes, Activity, List, UserPlus, CheckCircle2, RotateCcw, AlertTriangle } from 'lucide-react';

const TABS = [
  { key: 'config', label: 'Config', icon: Settings },
  { key: 'mapping', label: 'Field Mapping', icon: Map },
  { key: 'test', label: 'Test Payload', icon: TestTubes },
  { key: 'events', label: 'Events', icon: List },
  { key: 'health', label: 'Health', icon: Activity },
];

// Estados que acepta el backend para una fuente, en palabras del negocio.
const STATUS_OPTIONS = [
  { key: 'active', label: 'Activa', help: 'Recibe leads normalmente y los deja listos para pasarlos al CRM.' },
  { key: 'testing', label: 'En pruebas', help: 'Sigue recibiendo leads, pero queda marcada como "todavía la estoy configurando".' },
  { key: 'inactive', label: 'Apagada', help: 'Deja de recibir leads: quien intente enviarlos recibirá un error.' },
];
const STATUS_LABELS = { active: 'Activa', testing: 'En pruebas', inactive: 'Apagada' };

// Estados de cada lead recibido, en palabras del negocio.
const EVENT_STATUS_LABELS = {
  received: 'Recibido',
  staged: 'Listo para el CRM',
  converted: 'Convertido',
  duplicated: 'Repetido',
  failed: 'Con error',
  dead_letter: 'Con error (varios intentos)',
  ignored: 'Ignorado',
};
// Solo estos pueden volver a procesarse (lead_sources.py: reprocess_event).
const REPROCESSABLE = ['failed', 'dead_letter', 'ignored'];

const MAPPING_CHOICES = [
  { key: 'original', label: 'Usar la misma relación de campos de antes', help: 'Sirve si el problema fue pasajero (por ejemplo, una falla momentánea al recibirlo).' },
  { key: 'latest', label: 'Usar la relación de campos más reciente', help: 'Elige esta opción si ya corregiste los campos en la pestaña Field Mapping.' },
];

export default function LeadSourceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [source, setSource] = useState(null);
  const [tab, setTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(null);
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [testPayload, setTestPayload] = useState('{"name":"Test","phone":"5512345678","email":"test@example.com","message":"Hello"}');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [reprocessEvent, setReprocessEvent] = useState(null);
  const [reprocessMapping, setReprocessMapping] = useState('original');
  const [reprocessLoading, setReprocessLoading] = useState(false);

  const fetchSource = useCallback(async () => {
    try {
      const { data } = await api.get(`/tenant/lead-sources/${id}`);
      setSource(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchSource(); }, [fetchSource]);

  const fetchTab = async (t) => {
    setTab(t);
    try {
      if (t === 'mapping') {
        const { data } = await api.get(`/tenant/lead-sources/${id}/field-mapping`);
        setMapping(data);
      } else if (t === 'events') {
        const { data } = await api.get(`/tenant/lead-sources/${id}/events?limit=30`);
        setEvents(data);
      } else if (t === 'health') {
        const { data } = await api.get(`/tenant/lead-sources/${id}/health`);
        setHealth(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleTest = async () => {
    setTestLoading(true);
    try {
      const payload = JSON.parse(testPayload);
      const { data } = await api.post(`/tenant/lead-sources/${id}/test-payload`, { payload });
      setTestResult(data);
      toast.success(`Test: ${data.status}`);
    } catch (e) {
      if (e instanceof SyntaxError) toast.error('JSON inválido');
      else toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setTestLoading(false); }
  };

  const handleRegenerate = async () => {
    if (!confirm('¿Regenerar secret? El anterior dejará de funcionar.')) return;
    try {
      const { data } = await api.post(`/tenant/lead-sources/${id}/regenerate-secret`);
      toast.success('Secret regenerado');
      navigator.clipboard.writeText(data.webhook_secret);
      toast.success('Nuevo secret copiado al portapapeles');
      fetchSource();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleChangeStatus = async (next) => {
    if (!source || next === source.status || statusSaving) return;
    if (next === 'inactive' && !window.confirm(
      'Al apagarla, esta fuente dejará de aceptar leads entrantes: quien intente enviarlos recibirá un error.\n\n' +
      'Si esta fuente se creó desde Captación de Leads, también dejará de aparecer en esa pantalla hasta que la vuelvas a encender aquí.\n\n' +
      '¿Quieres apagarla?'
    )) return;
    setStatusSaving(true);
    try {
      await api.patch(`/tenant/lead-sources/${id}/status?status=${next}`);
      toast.success(
        next === 'active' ? 'Fuente activada: ya está recibiendo leads'
          : next === 'testing' ? 'Fuente marcada como "en pruebas": sigue recibiendo leads'
            : 'Fuente apagada: dejó de recibir leads'
      );
      await fetchSource();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar el estado de la fuente');
    } finally { setStatusSaving(false); }
  };

  const openReprocess = (ev) => {
    setReprocessMapping('original');
    setReprocessEvent(ev);
  };

  const handleReprocess = async () => {
    if (!reprocessEvent) return;
    setReprocessLoading(true);
    try {
      const { data } = await api.post(`/tenant/lead-sources/events/${reprocessEvent.id}/reprocess?use_mapping=${reprocessMapping}`);
      if (data?.status === 'ignored') {
        toast.success('Se volvió a procesar, pero quedó ignorado porque el contacto no dio su consentimiento.');
      } else {
        toast.success('Listo: se creó un registro nuevo con la información corregida. Ya puedes agregarlo al CRM.');
      }
      setReprocessEvent(null);
      await fetchTab('events');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo volver a procesar este lead');
    } finally { setReprocessLoading(false); }
  };

  if (loading) return <PantallaCargando />;
  if (!source) return <p className="text-[#94A3B8] text-center py-10">Fuente no encontrada</p>;

  return (
    <div data-testid="lead-source-detail">
      <button onClick={() => navigate('/tenant/integrations/lead-sources')} className="text-xs text-[#94A3B8] hover:text-[#0F172A] mb-3 flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Fuentes de Leads
      </button>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">{source.source_name}</h1>
          <p className="text-xs text-[#64748B]">{source.provider} — {source.medium} — {source.channel}
            {source.environment === 'sandbox' && <span className="ml-2 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">SANDBOX</span>}
          </p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${
          source.status === 'active' ? 'bg-emerald-50 text-emerald-600'
            : source.status === 'testing' ? 'bg-amber-50 text-amber-600'
              : 'bg-[#F1F5F9] text-[#94A3B8]'
        }`} data-testid="lead-source-status-badge">{STATUS_LABELS[source.status] || source.status}</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white border border-[#E2E8F0] rounded-lg p-0.5 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => fetchTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-all ${tab === t.key ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === 'config' && (
        <div className="space-y-4">
          {/* Encender / apagar la fuente */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="lead-source-status-panel">
            <div className="flex items-center gap-1.5 mb-1">
              <h3 className="text-sm font-semibold text-[#0F172A]">¿Esta fuente está recibiendo leads?</h3>
              <HelpTooltip text="Controla si esta fuente puede seguir recibiendo leads. Si la apagas, el formulario, la campaña o la herramienta que envía los leads recibirá un error hasta que la vuelvas a encender desde aquí. Nada de lo ya recibido se borra." />
            </div>
            <p className="text-xs text-[#64748B] mb-3">
              Hoy está: <strong className="text-[#0F172A]">{STATUS_LABELS[source.status] || source.status}</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(o => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => handleChangeStatus(o.key)}
                  disabled={statusSaving}
                  title={o.help}
                  data-testid={`lead-source-status-${o.key}`}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all disabled:opacity-40 ${
                    source.status === o.key
                      ? 'bg-[#0F172A] text-white border-[#0F172A]'
                      : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#CBD5E1] hover:text-[#0F172A]'
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
            <ul className="text-[11px] text-[#94A3B8] mt-3 space-y-0.5 leading-relaxed">
              {STATUS_OPTIONS.map(o => (
                <li key={o.key}><strong className="text-[#64748B]">{o.label}:</strong> {o.help}</li>
              ))}
            </ul>
            {statusSaving && <p className="text-[11px] text-[#64748B] mt-2 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Guardando el cambio…</p>}
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Webhook</h3>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-[#64748B]">URL</label>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-[#F1F5F9] px-3 py-2 rounded block flex-1 break-all" data-testid="webhook-url">{source.webhook_url}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(source.webhook_url); toast.success('Copiado'); }}><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Secret (enmascarado)</label>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-[#F1F5F9] px-3 py-2 rounded block flex-1 font-mono" data-testid="webhook-secret">{source.webhook_secret_masked}</code>
                  <Button size="sm" variant="outline" onClick={handleRegenerate}><RefreshCw className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Atribución</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[#94A3B8]">Provider:</span> <strong>{source.provider}</strong></div>
              <div><span className="text-[#94A3B8]">Medio:</span> <strong>{source.medium}</strong></div>
              <div><span className="text-[#94A3B8]">Canal:</span> <strong>{source.channel}</strong></div>
              <div><span className="text-[#94A3B8]">Campaña:</span> <strong>{source.campaign_name || '-'}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Mapping Tab */}
      {tab === 'mapping' && mapping && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0F172A]">Field Mapping v{mapping.current_version}</h3>
            <Button size="sm" variant="outline" onClick={async () => {
              const { data } = await api.post(`/tenant/lead-sources/${id}/suggest-mapping`);
              setMapping(prev => ({ ...prev, suggested: data.suggested_mappings }));
              toast.success('Sugerencias cargadas');
            }}>Sugerir Mappings</Button>
          </div>
          {(mapping.mappings || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-4">Sin mappings. Usa "Sugerir" o configura manualmente.</p>
          ) : (
            <div className="space-y-1.5">
              {mapping.mappings.map((m, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-[#F8F9FA]">
                  <code className="bg-[#F1F5F9] px-2 py-0.5 rounded">{m.external_field}</code>
                  <span className="text-[#94A3B8]">→</span>
                  <code className="bg-[#F1F5F9] px-2 py-0.5 rounded">{m.internal_field}</code>
                  {m.transform_type && <span className="text-[#94A3B8]">({m.transform_type})</span>}
                  {m.required && <span className="text-red-400">*</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Test Tab */}
      {tab === 'test' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Test Payload</h3>
            <textarea className="w-full px-3 py-2 text-xs border border-[#E2E8F0] rounded-md font-mono resize-none" rows={5}
              value={testPayload} onChange={e => setTestPayload(e.target.value)} data-testid="test-payload-input" />
            <Button size="sm" className="mt-2 bg-[#0F172A] text-white" onClick={handleTest} disabled={testLoading} data-testid="test-payload-btn">
              {testLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Ejecutar Test
            </Button>
          </div>
          {testResult && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Resultado: <span className={testResult.status === 'staged' ? 'text-emerald-600' : 'text-amber-600'}>{testResult.status}</span></h3>
              <div className="space-y-3 text-xs">
                <div><strong>Normalized:</strong><pre className="bg-[#F1F5F9] p-2 rounded mt-1 overflow-auto max-h-32">{JSON.stringify(testResult.normalized_payload, null, 2)}</pre></div>
                <div><strong>CRM Ready:</strong><pre className="bg-[#F1F5F9] p-2 rounded mt-1 overflow-auto max-h-32">{JSON.stringify(testResult.crm_ready, null, 2)}</pre></div>
                <div><strong>Dedup Key:</strong> <code>{testResult.deduplication_key}</code></div>
                <div><strong>Consent:</strong> {testResult.consent_status}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Events Tab */}
      {tab === 'events' && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center gap-1.5 mb-1">
            <h3 className="text-sm font-semibold text-[#0F172A]">{events.length} Eventos</h3>
            <HelpTooltip text="Cada renglón es un lead que llegó por esta fuente. Si alguno dice 'Con error' o 'Ignorado', puedes volver a procesarlo: se lee de nuevo la información original y se crea un registro nuevo, sin borrar el anterior." />
          </div>
          <p className="text-[11px] text-[#94A3B8] mb-3">Si un lead falló porque los campos no coincidían, corrígelos en la pestaña <strong>Field Mapping</strong> y vuelve a procesarlo con la relación de campos más reciente.</p>
          {events.length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-4">Sin registros todavía.</p>
          ) : (
            <div className="space-y-2">
              {events.map(e => (
                <div key={e.id} className="flex items-center justify-between py-2 border-b border-[#F8F9FA] last:border-0" data-testid={`event-${e.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        e.status === 'staged' ? 'bg-emerald-50 text-emerald-600' :
                        e.status === 'duplicated' ? 'bg-blue-50 text-blue-600' :
                        e.status === 'failed' ? 'bg-red-50 text-red-600' :
                        e.status === 'dead_letter' ? 'bg-red-50 text-red-600' :
                        'bg-[#F1F5F9] text-[#94A3B8]'
                      }`}>{EVENT_STATUS_LABELS[e.status] || e.status}</span>
                      {e.environment === 'sandbox' && <span className="text-[10px] bg-amber-50 text-amber-600 px-1 rounded">SANDBOX</span>}
                      {e.reprocessed_from_id && <span className="text-[10px] bg-[#F1F5F9] text-[#64748B] px-1 rounded" title="Este registro se creó al volver a procesar otro que había fallado">Reprocesado</span>}
                      <span className="text-xs text-[#0F172A]">{e.contact_name || e.contact_email || e.contact_phone || 'Sin datos'}</span>
                    </div>
                    <p className="text-[11px] text-[#94A3B8]">{e.received_at ? new Date(e.received_at).toLocaleString() : ''}</p>
                    {(e.error_code || e.error_message) && (
                      <p className="text-[11px] text-red-500 flex items-start gap-1 mt-0.5">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-[1px]" />
                        <span>Motivo: {[e.error_code, e.error_message].filter(Boolean).join(' — ')}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-[#94A3B8]">{e.deduplication_key?.slice(-12)}</span>
                    {e.status === 'converted' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-0.5" data-testid={`lead-source-converted-${e.id}`}>
                        <CheckCircle2 className="w-2.5 h-2.5" /> En CRM
                      </span>
                    )}
                    {e.status === 'staged' && e.environment !== 'sandbox' && (
                      <SensitiveActionHelp action={sensitiveActions.crm_convert_inbound} onConfirm={async () => {
                        try {
                          const { data } = await api.post(`/tenant/crm/inbound/${e.id}/convert`, { create_deal: true });
                          toast.success(`Lead agregado al CRM: ${data.lead?.display_name || 'OK'}`);
                          fetchTab(tab);
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Error al convertir'); }
                      }}>
                        <button className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center gap-0.5 disabled:opacity-40"
                          disabled={e.consent_status === 'denied'}
                          title={e.consent_status === 'denied' ? 'Consentimiento denegado' : 'Agregar al CRM'}
                          data-testid={`lead-source-convert-${e.id}`}>
                          <UserPlus className="w-2.5 h-2.5" /> Agregar al CRM
                        </button>
                      </SensitiveActionHelp>
                    )}
                    {REPROCESSABLE.includes(e.status) && (
                      <button
                        type="button"
                        onClick={() => openReprocess(e)}
                        title="Volver a procesar este lead con la información original"
                        data-testid={`lead-source-reprocess-${e.id}`}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-0.5">
                        <RotateCcw className="w-2.5 h-2.5" /> Volver a procesar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Health Tab */}
      {tab === 'health' && health && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Health</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="text-center"><p className="text-2xl font-bold text-[#0F172A]">{health.events_24h}</p><p className="text-xs text-[#94A3B8]">24h</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-[#0F172A]">{health.events_7d}</p><p className="text-xs text-[#94A3B8]">7 días</p></div>
          </div>
          <div className="space-y-1.5">
            {Object.entries(health.status_breakdown_7d || {}).map(([s, c]) => (
              <div key={s} className="flex items-center justify-between text-xs">
                <span className="text-[#475569]">{s}</span>
                <span className="font-medium text-[#0F172A] bg-[#F1F5F9] px-2 py-0.5 rounded">{c}</span>
              </div>
            ))}
          </div>
          {health.last_error_code && (
            <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
              <p className="text-xs text-red-500">Último error: {health.last_error_code} — {health.last_error_message}</p>
            </div>
          )}
        </div>
      )}

      {/* Volver a procesar un lead que falló */}
      <Dialog open={!!reprocessEvent} onOpenChange={(open) => { if (!open && !reprocessLoading) setReprocessEvent(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Volver a procesar este lead</DialogTitle>
            <DialogDescription className="text-xs">
              Se vuelve a leer la información tal como llegó y se crea un registro nuevo, listo para revisarlo. El registro con error se conserva: no se borra ni se modifica nada.
            </DialogDescription>
          </DialogHeader>
          {reprocessEvent && (
            <div className="space-y-3">
              <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 text-xs">
                <p className="text-[#0F172A] font-medium">{reprocessEvent.contact_name || reprocessEvent.contact_email || reprocessEvent.contact_phone || 'Sin datos de contacto'}</p>
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  Estado actual: {EVENT_STATUS_LABELS[reprocessEvent.status] || reprocessEvent.status}
                  {reprocessEvent.received_at ? ` · Recibido el ${new Date(reprocessEvent.received_at).toLocaleString()}` : ''}
                </p>
                {(reprocessEvent.error_code || reprocessEvent.error_message) && (
                  <p className="text-[11px] text-red-500 mt-1">Motivo: {[reprocessEvent.error_code, reprocessEvent.error_message].filter(Boolean).join(' — ')}</p>
                )}
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <p className="text-xs font-medium text-[#0F172A]">¿Con qué relación de campos quieres procesarlo?</p>
                  <HelpTooltip text="La relación de campos indica qué dato que llega (por ejemplo 'telefono') corresponde a cada campo del CRM. La configuras en la pestaña Field Mapping de esta misma pantalla." />
                </div>
                <div className="space-y-2">
                  {MAPPING_CHOICES.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setReprocessMapping(opt.key)}
                      data-testid={`reprocess-mapping-${opt.key}`}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        reprocessMapping === opt.key ? 'border-[#0F172A] bg-[#F8FAFC]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                      }`}>
                      <span className="block text-xs font-medium text-[#0F172A]">{opt.label}</span>
                      <span className="block text-[11px] text-[#94A3B8] mt-0.5">{opt.help}</span>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                Después de procesarlo, el registro nuevo aparecerá como <strong>Listo para el CRM</strong> y podrás agregarlo con el botón "Agregar al CRM" de esta misma lista.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setReprocessEvent(null)} disabled={reprocessLoading} data-testid="reprocess-cancel-btn">
              Cancelar
            </Button>
            <Button size="sm" className="bg-[#0F172A] text-white" onClick={handleReprocess} disabled={reprocessLoading} data-testid="reprocess-confirm-btn">
              {reprocessLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
              {reprocessLoading ? 'Procesando…' : 'Volver a procesar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
